<template>
  <div class="container-fluid p-0">
    <div class="row">
      <div class="col-12">
        <div class="card my-4">
          <div class="card-body p-0">
            <!-- Terminal Container -->
            <div class="terminal-container">
              <!-- Terminal Header -->
              <div class="terminal-header">
                <div class="terminal-controls">
                  <span class="control close"></span>
                  <span class="control minimize"></span>
                  <span class="control maximize"></span>
                </div>
                <div class="terminal-title">
                  <i class="fas fa-terminal"></i>
                  rifa365@logs:~$ tail -f /var/log/system.log
                </div>
                <div class="terminal-info">
                  {{ logsCount }} registros |
                  {{ isConnected ? "Conectado" : "Desconectado" }}
                </div>
              </div>

              <!-- Dashboard resumo -->
              <div class="terminal-stats" v-if="stats">
                <div class="stat-card">
                  <span class="stat-label">Hoje</span>
                  <strong>{{ stats.total_today || 0 }}</strong>
                  <small>eventos</small>
                </div>
                <div class="stat-card">
                  <span class="stat-label">Logins</span>
                  <strong>{{ stats.logins_today || 0 }}</strong>
                  <small>hoje</small>
                </div>
                <div class="stat-card">
                  <span class="stat-label">Usuários ativos</span>
                  <strong>{{ stats.active_users_today || 0 }}</strong>
                  <small>hoje</small>
                </div>
                <div class="stat-card alert" v-if="suspiciousCount > 0">
                  <span class="stat-label">Alertas</span>
                  <strong>{{ suspiciousCount }}</strong>
                  <small>suspeitos (7d)</small>
                </div>
              </div>

              <!-- Filters -->
              <div class="terminal-filters">
                <div class="filter-row">
                  <div class="filter-group">
                    <AutoComplete
                      v-model="selectedUser"
                      dropdown
                      optionLabel="full_name"
                      @complete="searchUsers"
                      @option-select="handleUserFilter"
                      @clear="clearUserFilter"
                      :suggestions="filteredUsers"
                      placeholder="Filtrar por usuário..."
                      class="terminal-input-small"
                      emptySearchMessage="Nenhum usuário encontrado"
                    >
                      <template #option="slotProps">
                        <div class="user-option">
                          <div class="user-name">
                            {{ slotProps.option.full_name }}
                          </div>
                          <div class="user-details">
                            ID: {{ slotProps.option.id }}
                            <span v-if="slotProps.option.email">
                              | {{ slotProps.option.email }}</span
                            >
                          </div>
                        </div>
                      </template>
                    </AutoComplete>
                  </div>

                  <div class="filter-group">
                    <select
                      v-model="moduleFilter"
                      @change="handleModuleFilter"
                      class="terminal-input-small"
                    >
                      <option value="">Todos os módulos</option>
                      <option value="AUTH">AUTH</option>
                      <option value="CAMPANHAS">CAMPANHAS</option>
                      <option value="PEDIDOS">PEDIDOS</option>
                      <option value="USUÁRIOS">USUÁRIOS</option>
                      <option value="FINANCEIRO">FINANCEIRO</option>
                      <option value="CONFIGURAÇÕES">CONFIGURAÇÕES</option>
                      <option value="SISTEMA">SISTEMA</option>
                    </select>
                  </div>

                  <div class="filter-group">
                    <select
                      v-model="severityFilter"
                      @change="handleSeverityFilter"
                      class="terminal-input-small"
                    >
                      <option value="">Todas severidades</option>
                      <option value="info">Info</option>
                      <option value="warning">Aviso</option>
                      <option value="error">Erro</option>
                    </select>
                  </div>

                  <div class="filter-group">
                    <input
                      v-model="searchDescription"
                      @input="handleSearch"
                      type="text"
                      class="terminal-input-small"
                      placeholder="Buscar nos logs..."
                    />
                  </div>

                  <div class="filter-group">
                    <select
                      v-model="dateFilter"
                      @change="handleDateFilter"
                      class="terminal-input-small"
                    >
                      <option value="all">Todos os Períodos</option>
                      <option value="today">Hoje</option>
                      <option value="week">Esta Semana</option>
                      <option value="month">Este Mês</option>
                      <option value="7days">Últimos 7 Dias</option>
                      <option value="30days">Últimos 30 Dias</option>
                    </select>
                  </div>

                  <div class="filter-actions">
                    <button @click="exportLogs" class="btn-terminal" :disabled="exporting">
                      <i class="fas fa-download"></i>
                    </button>
                    <button @click="clearAllFilters" class="btn-terminal">
                      Limpar
                    </button>
                    <button
                      @click="refreshLogs"
                      class="btn-terminal"
                      :disabled="loading"
                    >
                      <i
                        class="fas fa-sync-alt"
                        :class="{ 'fa-spin': loading }"
                      ></i>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Terminal Content -->
              <div
                class="terminal-content"
                ref="terminalContent"
                @scroll="handleScroll"
              >
                <!-- Welcome Message -->
                <div class="log-line system" v-if="logsCount === 0 && !loading">
                  <span class="timestamp">{{ getCurrentTimestamp() }}</span>
                  <span class="level system">SYSTEM</span>
                  <span class="message">
                    Terminal de logs inicializado. Aguardando stream de logs...
                    <span class="cursor">_</span>
                  </span>
                </div>

                <!-- Log Entries -->
                <div
                  v-for="log in safeLogsArray"
                  :key="log.id"
                  class="log-line"
                  :class="getLogTypeClass(log)"
                  @click="viewLogDetails(log)"
                >
                  <span class="timestamp">{{
                    formatLogTimestamp(log.created_at)
                  }}</span>
                  <span
                    class="level"
                    :class="getLogLevelClass(log)"
                  >
                    {{ getLogLevel(log) }}
                  </span>
                  <span class="user">{{ getLogUser(log.user) }}</span>
                  <span class="ip" v-if="log.ip">{{ log.ip }}</span>
                  <span
                    class="message"
                    v-html="formatLogMessage(log.description)"
                  ></span>
                  <span class="actions">
                    <i
                      class="fas fa-info-circle"
                      @click.stop="viewLogDetails(log)"
                    ></i>
                  </span>
                </div>

                <!-- Loading More -->
                <div class="log-line system" v-if="loading">
                  <span class="timestamp">{{ getCurrentTimestamp() }}</span>
                  <span class="level system">SYSTEM</span>
                  <span class="message">
                    Carregando mais logs...
                    <span class="loading-dots">
                      <span></span><span></span><span></span>
                    </span>
                  </span>
                </div>

                <!-- End of Logs -->
                <div
                  class="log-line system"
                  v-if="hasReachedEnd && logsCount > 0"
                >
                  <span class="timestamp">{{ getCurrentTimestamp() }}</span>
                  <span class="level system">SYSTEM</span>
                  <span class="message"
                    >Fim dos logs. Total de registros: {{ totalLogs }}</span
                  >
                </div>
              </div>

              <!-- Terminal Footer -->
              <div class="terminal-footer">
                <span>Linhas: {{ logsCount }}</span>
                <span>•</span>
                <span>{{ hasFilters ? "Filtrado" : "Todos os logs" }}</span>
                <div class="footer-actions">
                  <button @click="runRetentionCleanup" class="btn-footer" title="Limpar logs com mais de 90 dias">🗑</button>
                  <button @click="scrollToTop" class="btn-footer">↑</button>
                  <button @click="scrollToBottom" class="btn-footer">↓</button>
                  <button @click="toggleAutoRefresh" class="btn-footer">
                    {{ autoRefresh ? "⏸️" : "▶️" }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Detalhes -->
    <LogDetailsModal
      v-if="selectedLog"
      :isOpen="showDetailsModal"
      :log="selectedLog"
      @close="closeDetailsModal"
    />
  </div>
</template>

<script>
import http from "@/configs/AxiosConfig";
import LogsHttp from "@/http/LogsHttp";
import AutoComplete from "primevue/autocomplete";
import LogDetailsModal from "./components/LogDetailsModal.vue";

export default {
  name: "SystemLogs",
  components: {
    AutoComplete,
    LogDetailsModal,
  },
  data() {
    return {
      // ============================================================================
      // ESTADO / DADOS DO COMPONENTE
      // ============================================================================
      logs: [],                  // Lista de logs carregados
      loading: false,            // Estado de carregamento
      currentPage: 1,            // Página atual da paginação
      totalPages: 0,             // Total de páginas disponíveis
      totalLogs: 0,              // Total de logs encontrados
      hasReachedEnd: false,      // Flag para indicar que todos os logs foram carregados
      selectedUser: null,        // Usuário selecionado no filtro
      filteredUsers: [],         // Lista de usuários filtrados para o autocomplete
      searchDescription: "",     // Texto de busca na descrição
      moduleFilter: "",
      severityFilter: "",
      dateFilter: "all",         // Filtro de período (all, today, week, month)
      stats: null,
      exporting: false,
      selectedLog: null,         // Log selecionado para exibição de detalhes
      showDetailsModal: false,    // Controle de exibição do modal de detalhes
      isConnected: true,         // Estado da conexão com a API
      autoRefresh: false,        // Controle do auto-refresh
      autoRefreshInterval: null, // Intervalo do auto-refresh
      allUsers: [],              // Cache de todos os usuários para o autocomplete
      usersLoaded: false,        // Flag para indicar se os usuários já foram carregados
    };
  },
  computed: {
    // ============================================================================
    // PROPRIEDADES COMPUTADAS
    // ============================================================================
    
    /**
     * Garante que a lista de logs seja sempre um array válido
     */
    safeLogsArray() {
      return Array.isArray(this.logs) ? this.logs : [];
    },
    
    /**
     * Retorna a quantidade de logs carregados
     */
    logsCount() {
      return this.safeLogsArray.length || 0;
    },
    
    /**
     * Verifica se há algum filtro ativo
     */
    hasFilters() {
      return (
        this.selectedUser ||
        this.searchDescription ||
        this.moduleFilter ||
        this.severityFilter ||
        this.dateFilter !== "all"
      );
    },

    suspiciousCount() {
      return Array.isArray(this.stats?.suspicious) ? this.stats.suspicious.length : 0;
    },
  },
  mounted() {
    // Inicialização do componente
    this.fetchLogs();
    this.fetchStats();
    this.loadAllUsers();
  },
  beforeUnmount() {
    // Limpeza do intervalo de auto-refresh antes de destruir o componente
    if (this.autoRefreshInterval) {
      clearInterval(this.autoRefreshInterval);
    }
  },
  methods: {
    // ============================================================================
    // MÉTODOS DE DADOS E API
    // ============================================================================
    
    buildFilterParams() {
      const params = {};
      if (this.selectedUser) params.user_id = this.selectedUser.id;
      if (this.searchDescription) params.description = this.searchDescription;
      if (this.moduleFilter) params.module = this.moduleFilter;
      if (this.severityFilter) params.severity = this.severityFilter;
      if (this.dateFilter !== "all") params.period = this.dateFilter;
      return params;
    },

    async fetchStats() {
      try {
        const response = await LogsHttp.getStats();
        this.stats = response.data;
      } catch (error) {
        console.error("Erro ao buscar estatísticas de logs:", error);
      }
    },

    async exportLogs() {
      try {
        this.exporting = true;
        const response = await LogsHttp.exportLogs(this.buildFilterParams());
        const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `rifa365-logs-${Date.now()}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Erro ao exportar logs:", error);
        alert("Não foi possível exportar os logs.");
      } finally {
        this.exporting = false;
      }
    },

    async runRetentionCleanup() {
      if (!window.confirm("Remover logs com mais de 90 dias? Esta ação não pode ser desfeita.")) {
        return;
      }
      try {
        const response = await LogsHttp.cleanupOldLogs(90);
        alert(`Limpeza concluída. ${response.data.deleted || 0} registros removidos.`);
        this.refreshLogs();
        this.fetchStats();
      } catch (error) {
        console.error("Erro na limpeza de logs:", error);
        alert("Não foi possível executar a limpeza de logs.");
      }
    },

    /**
     * Gera logs mockados caso a API falhe ou não retorne dados
     */
    generateMockLogs() {
      return [
        {
          id: 1501,
          id_user: 108,
          description: "[Sistema][INICIAR] Terminal de logs iniciado com sucesso",
          user_agent: "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/142.0.0.0 Safari/537.36",
          ip: "127.0.0.1",
          created_at: new Date().toISOString().replace("T", " ").substring(0, 19),
          user: {
            id: 108,
            full_name: "Sistema",
            email: "system@rifa365.com",
            role: "SYSTEM",
          },
        },
        {
          id: 1502,
          id_user: 108,
          description: "[Campanhas][CRIAR] Nova campanha criada: iPhone 15 Pro Max",
          user_agent: "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/142.0.0.0 Safari/537.36",
          ip: "192.168.1.100",
          created_at: new Date(Date.now() - 60000).toISOString().replace("T", " ").substring(0, 19),
          user: {
            id: 108,
            full_name: "Admin User",
            email: "admin@rifa365.com",
            role: "ADMIN",
          },
        },
        {
          id: 1503,
          id_user: 109,
          description: "[Pedidos][PAGAMENTO] Pagamento confirmado - Valor: R$ 25,00",
          user_agent: "Mozilla/5.0 (iPhone; CPU iPhone OS 15_0 like Mac OS X) AppleWebKit/605.1.15",
          ip: "189.84.36.206",
          created_at: new Date(Date.now() - 120000).toISOString().replace("T", " ").substring(0, 19),
          user: {
            id: 109,
            full_name: "João Silva",
            email: "joao@exemplo.com",
            role: "USER",
          },
        },
        {
          id: 1504,
          id_user: 110,
          description: "Relatório acessado: Ranking",
          user_agent: "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36",
          ip: "189.84.36.206",
          created_at: new Date(Date.now() - 180000).toISOString().replace("T", " ").substring(0, 19),
          user: {
            id: 110,
            full_name: "Maria Santos",
            email: "maria@exemplo.com",
            role: "ADMIN",
          },
        },
        {
          id: 1505,
          id_user: 108,
          description: "[Configurações][ATUALIZAR] Dados pessoais atualizados com sucesso",
          user_agent: "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/142.0.0.0 Safari/537.36",
          ip: "189.84.36.206",
          created_at: new Date(Date.now() - 240000).toISOString().replace("T", " ").substring(0, 19),
          user: {
            id: 108,
            full_name: "Pedro Costa",
            email: "pedro@exemplo.com",
            role: "ADMIN",
          },
        },
      ];
    },

    /**
     * Garante a integridade do array de logs
     */
    ensureLogsArray() {
      if (!Array.isArray(this.logs)) {
        this.logs = [];
      }
    },

    /**
     * Busca os logs na API com base nos filtros e página
     */
    async fetchLogs(page = 1, append = false) {
      try {
        this.loading = true;

        const params = {
          page: page,
          limit: 50,
          ...this.buildFilterParams(),
        };

        const response = await http.get("/logs", { params });

        if (response.data && Array.isArray(response.data.logs)) {
          this.ensureLogsArray();

          if (append && Array.isArray(this.logs)) {
            this.logs = [...this.logs, ...response.data.logs];
          } else {
            this.logs = response.data.logs;
          }

          this.totalPages = response.data.pages || 1;
          this.totalLogs = response.data.total || this.logs.length;
          this.currentPage = page;
          this.hasReachedEnd = page >= (response.data.pages || 1);
        }
        this.isConnected = true;
      } catch (error) {
        console.error("Erro ao buscar logs:", error);
        this.isConnected = false;
        this.ensureLogsArray();
      } finally {
        this.loading = false;
      }
    },

    /**
     * Carrega mais logs (Infinite Scroll)
     */
    async loadMoreLogs() {
      this.ensureLogsArray();
      if (!this.loading && !this.hasReachedEnd && Array.isArray(this.logs)) {
        await this.fetchLogs(this.currentPage + 1, true);
      }
    },

    /**
     * Carrega a lista mínima de usuários para o autocomplete
     */
    async loadAllUsers() {
      if (this.usersLoaded) return;

      try {
        const response = await http.get("/admin/users/minimum");
        this.allUsers = Array.isArray(response.data) ? response.data : [];
        this.usersLoaded = true;
      } catch (error) {
        console.error("Erro ao carregar usuários:", error);
        this.allUsers = [];
      }
    },

    // ============================================================================
    // MÉTODOS DE EVENTOS E INTERAÇÃO
    // ============================================================================

    /**
     * Manipula o scroll do terminal para carregar mais logs
     */
    handleScroll() {
      const container = this.$refs.terminalContent;
      if (container) {
        try {
          const { scrollTop, scrollHeight, clientHeight } = container;
          const threshold = 100;

          if (scrollHeight - scrollTop - clientHeight < threshold) {
            this.loadMoreLogs();
          }
        } catch (error) {
          console.error("Erro no scroll handler:", error);
        }
      }
    },

    /**
     * Filtra os usuários localmente para o componente AutoComplete
     */
    async searchUsers(event) {
      try {
        await this.loadAllUsers();

        const query = event.query.toLowerCase().trim();

        if (!query) {
          this.filteredUsers = this.allUsers.slice(0, 20);
          return;
        }

        this.filteredUsers = this.allUsers
          .filter(
            (user) =>
              (user.full_name && user.full_name.toLowerCase().includes(query)) ||
              (user.email && user.email.toLowerCase().includes(query)) ||
              (user.id && user.id.toString().includes(query))
          )
          .slice(0, 20);
      } catch (error) {
        console.error("Erro ao filtrar usuários:", error);
        this.filteredUsers = [];
      }
    },

    /**
     * Aplica o filtro de usuário
     */
    handleUserFilter() {
      this.currentPage = 1;
      this.hasReachedEnd = false;
      this.fetchLogs();
    },

    /**
     * Limpa o filtro de usuário
     */
    clearUserFilter() {
      this.selectedUser = null;
      this.currentPage = 1;
      this.hasReachedEnd = false;
      this.fetchLogs();
    },

    /**
     * Aplica a busca por descrição
     */
    handleSearch() {
      this.currentPage = 1;
      this.hasReachedEnd = false;
      this.fetchLogs();
    },

    /**
     * Aplica o filtro de data
     */
    handleDateFilter() {
      this.currentPage = 1;
      this.hasReachedEnd = false;
      this.fetchLogs();
    },

    handleModuleFilter() {
      this.currentPage = 1;
      this.hasReachedEnd = false;
      this.fetchLogs();
    },

    handleSeverityFilter() {
      this.currentPage = 1;
      this.hasReachedEnd = false;
      this.fetchLogs();
    },

    /**
     * Limpa todos os filtros ativos
     */
    clearAllFilters() {
      this.selectedUser = null;
      this.searchDescription = "";
      this.moduleFilter = "";
      this.severityFilter = "";
      this.dateFilter = "all";
      this.currentPage = 1;
      this.hasReachedEnd = false;
      this.fetchLogs();
    },

    /**
     * Atualiza a lista de logs
     */
    refreshLogs() {
      this.currentPage = 1;
      this.hasReachedEnd = false;
      this.fetchLogs();
      this.fetchStats();
    },

    /**
     * Rola o terminal para o topo
     */
    scrollToTop() {
      if (this.$refs.terminalContent) {
        this.$refs.terminalContent.scrollTop = 0;
      }
    },

    /**
     * Rola o terminal para o final
     */
    scrollToBottom() {
      if (this.$refs.terminalContent) {
        this.$refs.terminalContent.scrollTop = this.$refs.terminalContent.scrollHeight;
      }
    },

    /**
     * Ativa/Desativa o auto-refresh a cada 30 segundos
     */
    toggleAutoRefresh() {
      this.autoRefresh = !this.autoRefresh;

      if (this.autoRefresh) {
        this.autoRefreshInterval = setInterval(() => {
          if (!this.hasFilters) {
            this.refreshLogs();
          }
        }, 30000);
      } else {
        if (this.autoRefreshInterval) {
          clearInterval(this.autoRefreshInterval);
          this.autoRefreshInterval = null;
        }
      }
    },

    /**
     * Abre o modal com detalhes do log
     */
    viewLogDetails(log) {
      this.selectedLog = log;
      this.showDetailsModal = true;
    },

    /**
     * Fecha o modal de detalhes
     */
    closeDetailsModal() {
      this.showDetailsModal = false;
      this.selectedLog = null;
    },

    // ============================================================================
    // MÉTODOS DE FORMATAÇÃO E UI
    // ============================================================================

    /**
     * Retorna o timestamp atual formatado
     */
    getCurrentTimestamp() {
      return new Date().toLocaleString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });
    },

    /**
     * Formata o timestamp do log para exibição rápida (DD/MM/YYYY, HH:MM:SS)
     */
    formatLogTimestamp(dateString) {
      try {
        const [date, time] = dateString.split(" ");
        const [year, month, day] = date.split("-");
        const [hours, minutes, seconds] = time.split(":");
        return `${day}/${month}/${year}, ${hours}:${minutes}:${seconds}`;
      } catch (error) {
        return "00:00:00";
      }
    },

    /**
     * Determina o nível do log com base na descrição (para estilização)
     */
    getLogLevel(logOrDescription) {
      const description = typeof logOrDescription === "string"
        ? logOrDescription
        : logOrDescription?.description;
      const moduleName = typeof logOrDescription === "object" ? logOrDescription?.module : "";
      const actionName = typeof logOrDescription === "object" ? logOrDescription?.action : "";

      if (!description && !moduleName) return "INFO";

      if (moduleName === "AUTH" || actionName === "LOGIN" || actionName === "LOGOUT" || actionName === "LOGIN_FALHA")
        return "AUTH";
      if (moduleName === "SISTEMA" && (actionName === "ERRO" || description?.includes("[ERRO]")))
        return "ERROR";
      if (
        actionName === "CRIAR" ||
        (moduleName === "CAMPANHAS" && description?.includes("criou"))
      )
        return "CREATE";
      if (
        actionName === "EDITAR" || actionName === "ATUALIZAR" ||
        description?.includes("editou") ||
        description?.includes("atualizou")
      )
        return "UPDATE";
      if (
        actionName === "DELETAR" || actionName === "DESATIVAR" ||
        description?.includes("cancelou") ||
        description?.includes("deletou")
      )
        return "DELETE";
      if (moduleName === "FINANCEIRO") return "FINANCE";
      if (moduleName === "PEDIDOS") return "ORDERS";
      if (moduleName === "CAMPANHAS") return "CAMPAIGNS";
      if (moduleName === "CONFIGURAÇÕES" || moduleName === "SISTEMA") return "SYSTEM";
      
      return "INFO";
    },

    getLogLevelClass(logOrDescription) {
      const level = this.getLogLevel(logOrDescription);
      return level.toLowerCase();
    },

    getLogTypeClass(logOrDescription) {
      const level = this.getLogLevel(logOrDescription);
      return `log-${level.toLowerCase()}`;
    },

    /**
     * Retorna o identificador do usuário (email ou nome)
     */
    getLogUser(user) {
      if (!user) return "system@rifa365";
      return user.email || user.full_name || "unknown@user";
    },

    /**
     * Formata a mensagem do log aplicando filtros de segurança e spans coloridos
     */
    formatLogMessage(description) {
      if (!description) return "";

      // Limpa tags HTML perigosas
      let cleaned = description.replace(/<[^>]*>/g, "");
      cleaned = cleaned
        .replace(/&amp;/g, "&")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'");

      let formatted = cleaned;

      // Destaca textos entre aspas
      formatted = formatted.replace(
        /"([^"]+)"/g,
        '"<span class="log-name">$1</span>"'
      );

      // Destaca valores monetários
      formatted = formatted.replace(
        /(R\$\s?[\d.,]+)/g,
        '<span class="log-value">$1</span>'
      );

      // Destaca palavras de status
      formatted = formatted.replace(
        /\b(ATIVA|ATIVO|PAGO|APROVADO|CANCELADO|REJEITADO)\b/g,
        '<span class="log-success">$1</span>'
      );

      // Destaca erros
      formatted = formatted.replace(
        /\b(Erro|ERRO|falha)\b/gi,
        '<span class="log-error">$1</span>'
      );

      // Destaca módulos e ações entre colchetes com cores específicas
      let bracketCount = 0;
      formatted = formatted.replace(/\[([^\]]+)\]/g, (match, content) => {
        bracketCount++;
        if (bracketCount === 1) {
          const moduleColors = {
            "AUTH":          "#bd93f9",
            "CAMPANHAS":     "#8be9fd",
            "PEDIDOS":       "#50fa7b",
            "FINANCEIRO":    "#f1fa8c",
            "USUÁRIOS":      "#ffb86c",
            "CONFIGURAÇÕES": "#ff79c6",
            "SISTEMA":       "#6272a4",
            "RANKING":       "#50fa7b",
            "DASHBOARD":     "#8be9fd",
            "AFILIADOS":     "#ffb86c",
          };
          const color = moduleColors[content.toUpperCase()] || "#8be9fd";
          return `<span class="log-module" style="color:${color}">[${content}]</span>`;
        } else {
          return `<span class="log-action">[${content}]</span>`;
        }
      });

      return formatted;
    },
  },
};
</script>

<style scoped>
/* Dracula Theme Colors - Apenas para este componente */
.terminal-container {
  --dracula-bg: #282a36;
  --dracula-current-line: #44475a;
  --dracula-selection: #44475a;
  --dracula-foreground: #f8f8f2;
  --dracula-comment: #6272a4;
  --dracula-cyan: #8be9fd;
  --dracula-green: #50fa7b;
  --dracula-orange: #ffb86c;
  --dracula-pink: #ff79c6;
  --dracula-purple: #bd93f9;
  --dracula-red: #ff5555;
  --dracula-yellow: #f1fa8c;

  background: var(--dracula-bg);
  color: var(--dracula-foreground);
  font-family: "Fira Code", "Consolas", "Monaco", monospace;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: inset 0 0 0 1px var(--dracula-current-line);
}

/* Terminal Header */
.terminal-header {
  background: var(--dracula-current-line);
  padding: 8px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid var(--dracula-selection);
  font-size: 12px;
}

.terminal-controls {
  display: flex;
  gap: 6px;
}

.control {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  cursor: pointer;
}

.control.close {
  background: var(--dracula-red);
}
.control.minimize {
  background: var(--dracula-yellow);
}
.control.maximize {
  background: var(--dracula-green);
}

.terminal-title {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--dracula-green);
  font-weight: bold;
  font-size: 11px;
}

.terminal-info {
  color: var(--dracula-comment);
  font-size: 10px;
}

/* Stats dashboard */
.terminal-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 10px;
  padding: 12px 16px;
  background: var(--dracula-bg);
  border-bottom: 1px solid var(--dracula-selection);
}

.stat-card {
  background: var(--dracula-current-line);
  border: 1px solid var(--dracula-selection);
  border-radius: 6px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.stat-card.alert {
  border-color: var(--dracula-orange);
}

.stat-label {
  color: var(--dracula-comment);
  font-size: 10px;
  text-transform: uppercase;
}

.stat-card strong {
  color: var(--dracula-green);
  font-size: 20px;
}

.stat-card small {
  color: var(--dracula-comment);
  font-size: 10px;
}

.ip {
  color: var(--dracula-orange);
  min-width: 100px;
  font-size: 10px;
}

/* Filters */
.terminal-filters {
  background: var(--dracula-current-line);
  padding: 12px 16px;
  border-bottom: 1px solid var(--dracula-selection);
}

.filter-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.filter-group {
  min-width: 150px;
}

.terminal-input-small,
.terminal-input-small input {
  background: var(--dracula-bg);
  border: 1px solid var(--dracula-selection);
  color: var(--dracula-foreground);
  padding: 4px 8px;
  border-radius: 4px;
  font-family: inherit;
  font-size: 11px;
  width: 100%;
}

.terminal-input-small:focus {
  outline: none;
  border-color: var(--dracula-purple);
}

.filter-actions {
  display: flex;
  gap: 6px;
  margin-left: auto;
}

.btn-terminal {
  background: var(--dracula-purple);
  color: var(--dracula-bg);
  border: none;
  padding: 4px 8px;
  border-radius: 3px;
  font-size: 10px;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-terminal:hover:not(:disabled) {
  background: var(--dracula-pink);
}

.btn-terminal:disabled {
  background: var(--dracula-comment);
  cursor: not-allowed;
}

/* Terminal Content */
.terminal-content {
  background: var(--dracula-bg);
  height: 500px;
  overflow-y: auto;
  padding: 12px 16px;
  font-size: 11px;
  line-height: 1.4;
}

.terminal-content::-webkit-scrollbar {
  width: 8px;
}

.terminal-content::-webkit-scrollbar-track {
  background: var(--dracula-current-line);
}

.terminal-content::-webkit-scrollbar-thumb {
  background: var(--dracula-comment);
  border-radius: 4px;
}

.terminal-content::-webkit-scrollbar-thumb:hover {
  background: var(--dracula-purple);
}

/* Log Lines */
.log-line {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 2px 0;
  cursor: pointer;
  border-radius: 2px;
  transition: background 0.2s;
}

.log-line:hover {
  background: var(--dracula-current-line);
  padding-left: 4px;
  padding-right: 4px;
}

.timestamp {
  color: var(--dracula-comment);
  min-width: 70px;
  font-size: 10px;
}

.level {
  min-width: 60px;
  font-weight: bold;
  font-size: 9px;
  text-transform: uppercase;
  padding: 1px 4px;
  border-radius: 2px;
  text-align: center;
}

.level.create {
  background: var(--dracula-green);
  color: var(--dracula-bg);
}
.level.update {
  background: var(--dracula-yellow);
  color: var(--dracula-bg);
}
.level.delete {
  background: var(--dracula-red);
  color: var(--dracula-foreground);
}
.level.auth {
  background: var(--dracula-purple);
  color: var(--dracula-foreground);
}
.level.error {
  background: var(--dracula-red);
  color: var(--dracula-foreground);
}
.level.warn {
  background: var(--dracula-orange);
  color: var(--dracula-bg);
}
.level.info {
  background: var(--dracula-cyan);
  color: var(--dracula-bg);
}
.level.system {
  background: var(--dracula-comment);
  color: var(--dracula-foreground);
}

.user {
  color: var(--dracula-purple);
  min-width: 150px;
  font-size: 10px;
}

.message {
  flex: 1;
  color: var(--dracula-foreground);
}

.message :deep(.log-module) {
  color: var(--dracula-pink);
  font-weight: bold;
}

.message :deep(.log-id) {
  color: var(--dracula-orange);
  font-weight: bold;
}

.message :deep(.log-success) {
  color: var(--dracula-green);
  font-weight: bold;
}

.message :deep(.log-error) {
  color: var(--dracula-red);
  font-weight: bold;
}

.actions {
  color: var(--dracula-comment);
  cursor: pointer;
  padding: 2px;
  border-radius: 2px;
  transition: color 0.2s;
}

.actions:hover {
  color: var(--dracula-cyan);
}

/* System Messages */
.log-line.system {
  opacity: 0.7;
}

.log-line.system .message {
  color: var(--dracula-comment);
  font-style: italic;
}

/* Loading Animation */
.loading-dots {
  display: inline-flex;
  gap: 2px;
  margin-left: 8px;
}

.loading-dots span {
  width: 3px;
  height: 3px;
  background: var(--dracula-cyan);
  border-radius: 50%;
  animation: loading-dots 1.4s infinite ease-in-out;
}

.loading-dots span:nth-child(1) {
  animation-delay: -0.32s;
}
.loading-dots span:nth-child(2) {
  animation-delay: -0.16s;
}

@keyframes loading-dots {
  0%,
  80%,
  100% {
    opacity: 0.3;
    transform: scale(0.8);
  }
  40% {
    opacity: 1;
    transform: scale(1);
  }
}

/* Cursor */
.cursor {
  display: inline-block;
  color: var(--dracula-foreground);
  animation: cursor-blink 1s infinite;
  margin-left: 4px;
}

@keyframes cursor-blink {
  0%,
  50% {
    opacity: 1;
  }
  51%,
  100% {
    opacity: 0;
  }
}

/* Terminal Footer */
.terminal-footer {
  background: var(--dracula-current-line);
  padding: 6px 16px;
  border-top: 1px solid var(--dracula-selection);
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 10px;
  color: var(--dracula-comment);
}

.footer-actions {
  display: flex;
  gap: 4px;
}

.btn-footer {
  background: none;
  border: 1px solid var(--dracula-selection);
  color: var(--dracula-foreground);
  padding: 2px 6px;
  border-radius: 2px;
  font-size: 9px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-footer:hover {
  background: var(--dracula-purple);
  border-color: var(--dracula-purple);
}

/* AutoComplete Customization */
.terminal-input-small :deep(.p-autocomplete-input) {
  background: var(--dracula-bg) !important;
  border: 1px solid var(--dracula-selection) !important;
  color: var(--dracula-foreground) !important;
  padding: 4px 8px !important;
  border-radius: 4px !important;
  font-family: inherit !important;
  font-size: 11px !important;
}

.terminal-input-small :deep(.p-autocomplete-panel) {
  background: var(--dracula-current-line) !important;
  border: 1px solid var(--dracula-selection) !important;
  color: var(--dracula-foreground) !important;
}

.terminal-input-small :deep(.p-autocomplete-items .p-autocomplete-item) {
  background: var(--dracula-current-line) !important;
  color: var(--dracula-foreground) !important;
  padding: 6px 8px !important;
  border: none !important;
  font-size: 11px !important;
}

.terminal-input-small :deep(.p-autocomplete-items .p-autocomplete-item:hover) {
  background: var(--dracula-purple) !important;
  color: var(--dracula-bg) !important;
}

/* User Option Styling */
.user-option {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.user-name {
  font-weight: bold;
  font-size: 11px;
}

.user-details {
  font-size: 9px;
  opacity: 0.8;
  color: var(--dracula-comment);
}

/* Responsive */
@media (max-width: 768px) {
  .filter-row {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-actions {
    margin-left: 0;
    justify-content: center;
  }

  .log-line {
    flex-direction: column;
    gap: 2px;
  }

  .timestamp,
  .level,
  .user {
    min-width: auto;
  }
}
</style>